export type ChatNavigationContactHint = {
  id?: number | string | null;
  phone?: string | null;
  active_channel?: string | null;
  source_channel?: string | null;
};

export type ChatNavigationState = {
  selectedPhone?: string | null;
  selectedContact?: ChatNavigationContactHint | null;
} | null | undefined;

export type ChatNavigationPayload = {
  rawPhone: string;
  normalizedPhone: string;
  selectedContactId: number;
  isInstagram: boolean;
};

const defaultNormalizePhone = (phone: string): string =>
  String(phone || '').replace(/\D/g, '');

export const isChatNavigationInstagramHint = (
  contact: ChatNavigationContactHint | null | undefined,
): boolean =>
  (contact?.active_channel || contact?.source_channel) === 'instagram';

export const readChatNavigationPayload = (
  state: ChatNavigationState,
  normalizePhone: (phone: string) => string = defaultNormalizePhone,
): ChatNavigationPayload | null => {
  const selectedContact = state?.selectedContact ?? null;
  const isInstagram = isChatNavigationInstagramHint(selectedContact);
  const rawPhone = isInstagram ? '' : String(state?.selectedPhone || '');

  if (!rawPhone && !isInstagram) {
    return null;
  }

  return {
    rawPhone,
    normalizedPhone: isInstagram ? '' : normalizePhone(rawPhone),
    selectedContactId: Number(selectedContact?.id || 0),
    isInstagram,
  };
};

export const isChatContactIdentityResolved = ({
  selectedContactId,
  hasPendingNavigation,
  archived = false,
  treatArchivedAsUnresolved = false,
}: {
  selectedContactId: number;
  hasPendingNavigation: boolean;
  archived?: boolean;
  treatArchivedAsUnresolved?: boolean;
}): boolean => {
  if (hasPendingNavigation) {
    return false;
  }

  if (!Number.isInteger(selectedContactId) || selectedContactId <= 0) {
    return false;
  }

  if (treatArchivedAsUnresolved && archived === true) {
    return false;
  }

  return true;
};

export const pickNavigationContact = <
  T extends {
    id: number;
    phone?: string | null;
    active_channel?: string | null;
    source_channel?: string | null;
  },
>(
  contacts: Iterable<T>,
  payload: ChatNavigationPayload,
  normalizePhone: (phone: string) => string = defaultNormalizePhone,
): T | undefined => {
  const list = Array.from(contacts);

  if (payload.selectedContactId > 0) {
    const byId = list.find((contact) => contact.id === payload.selectedContactId);
    if (byId) {
      return byId;
    }
  }

  if (payload.isInstagram) {
    return undefined;
  }

  const exactPhoneMatches = list.filter((contact) => {
    if (isChatNavigationInstagramHint(contact)) {
      return false;
    }
    return normalizePhone(String(contact.phone || '')) === payload.normalizedPhone;
  });

  return exactPhoneMatches.length === 1 ? exactPhoneMatches[0] : undefined;
};
